/**
 * The two non-business income methods (ADR-0004).
 *
 * Courier gigs: no capital, small pay, always there. The safety net that means
 * the player can never be truly stuck, which is what lets the rest of the
 * economy be tight without being unfair.
 *
 * Depot shifts: a fixed block of hours for fixed pay. This is the opportunity
 * cost every business decision is measured against - the player should always
 * be able to ask "is my own business beating a shift yet?".
 */
import type { GameState, CourierJob, ShiftOffer } from '../state/GameState.js';
import type { Rng } from '../core/rng.js';
import type { EventBus } from '../core/events.js';
import type { IdGenerator } from '../core/ids.js';
import type { FinanceSystem } from './FinanceSystem.js';
import { BALANCE } from '../data/balance.js';
import { LOCATIONS, CUSTOMER_LOTS } from '../data/world.js';

const PICKUP_IDS = ['depot', 'store', 'toolshop'];

export class JobSystem {
  constructor(
    private readonly bus: EventBus,
    private readonly rng: Rng,
    private readonly ids: IdGenerator,
  ) {}

  /**
   * Reset the courier board and today's shift offer. Called at each day start.
   *
   * Note this runs at midnight, outside the courier availability window, so it
   * cannot populate the board directly - it schedules the morning batch for the
   * hour the window opens instead. (An earlier version called the fill here and
   * it silently did nothing, leaving the board empty every day.)
   */
  refreshDaily(state: GameState): void {
    state.shiftsWorkedToday = 0;
    state.courierJobs = [];
    const dayStart = state.time.totalMinutes - state.time.minuteOfDay;
    state.nextCourierRefreshMinute =
      dayStart + BALANCE.jobs.courier.availableFromHour * 60;
    state.shiftOffer = this.makeShiftOffer(state);
  }

  /**
   * Top up the courier board.
   *
   * Refills are rate-limited rather than instant. An earlier version refilled
   * the board the moment a gig was handed in, which let the balance harness
   * farm gigs indefinitely - an unbounded income source that made the whole
   * equipment ladder optional. Supply is now the constraint that keeps gigs a
   * gap-filler: the board holds at most `concurrentOffers`, and adds one new
   * offer every `refreshMinutes`.
   */
  topUpCourierJobs(state: GameState): void {
    const cfg = BALANCE.jobs.courier;
    const hour = Math.floor(state.time.minuteOfDay / 60);
    if (hour < cfg.availableFromHour || hour >= cfg.availableToHour) return;
    if (state.time.totalMinutes < state.nextCourierRefreshMinute) return;
    if (state.courierJobs.length >= cfg.concurrentOffers) return;

    // The first top-up of the day fills the board; after that, one at a time.
    //
    // Detected from the scheduled opening minute, NOT from the board being
    // empty. Inferring it from an empty board meant a player who cleared the
    // board got a full batch back immediately, every time - an unbounded income
    // loop that the balance harness caught earning three times the intended
    // daily cap.
    const dayStart = state.time.totalMinutes - state.time.minuteOfDay;
    const openingMinute = dayStart + cfg.availableFromHour * 60;
    const isMorningBatch = state.nextCourierRefreshMinute === openingMinute;

    do {
      this.addCourierJob(state);
    } while (isMorningBatch && state.courierJobs.length < cfg.concurrentOffers);

    state.nextCourierRefreshMinute = state.time.totalMinutes + cfg.refreshMinutes;
  }

  private addCourierJob(state: GameState): void {
    const cfg = BALANCE.jobs.courier;
    const pickup = this.rng.pick(PICKUP_IDS);
    const dropoff = this.rng.pick(
      [...CUSTOMER_LOTS.map((l) => l.id), 'home', 'park'].filter((id) => id !== pickup),
    );
    const job: CourierJob = {
      id: this.ids.next('gig'),
      title: `Parcel to ${nameOf(dropoff)}`,
      pickupId: pickup,
      dropoffId: dropoff,
      pay: this.rng.int(cfg.minPay, cfg.maxPay),
      workMinutes: cfg.workMinutes,
      expiresAtMinute: state.time.totalMinutes + cfg.refreshMinutes * 3,
    };
    state.courierJobs.push(job);
    this.bus.emit({ type: 'job.offered', jobId: job.id, title: job.title });
  }

  expireCourierJobs(state: GameState): void {
    state.courierJobs = state.courierJobs.filter(
      (j) => j.expiresAtMinute > state.time.totalMinutes,
    );
  }

  private makeShiftOffer(state: GameState): ShiftOffer {
    const cfg = BALANCE.jobs.shift;
    return {
      id: this.ids.next('shift'),
      title: `Warehouse shift (${cfg.hours}h)`,
      locationId: 'depot',
      hourlyPay: cfg.hourlyPay,
      hours: cfg.hours,
      availableUntilMinute: cfg.startToHour * 60,
    };
  }

  canWorkShift(state: GameState): boolean {
    const cfg = BALANCE.jobs.shift;
    if (!state.shiftOffer) return false;
    if (state.shiftsWorkedToday >= cfg.perDay) return false;
    const hour = Math.floor(state.time.minuteOfDay / 60);
    return hour >= cfg.startFromHour && hour <= cfg.startToHour;
  }

  /**
   * Shifts are resolved as a block rather than minute by minute: the player
   * commits the hours and gets the pay. Returns the minutes consumed so the
   * caller can advance the clock.
   */
  workShift(
    state: GameState,
    finance: FinanceSystem,
  ): { ok: boolean; minutes: number; pay: number } {
    if (!this.canWorkShift(state) || !state.shiftOffer) {
      return { ok: false, minutes: 0, pay: 0 };
    }
    const offer = state.shiftOffer;
    const pay = offer.hourlyPay * offer.hours;
    finance.credit(state, pay, 'job', offer.title);
    state.shiftsWorkedToday += 1;
    state.statistics.shiftsCompleted += 1;
    this.bus.emit({
      type: 'job.completed',
      jobId: offer.id,
      title: offer.title,
      pay,
      quality: 1,
    });
    return { ok: true, minutes: offer.hours * 60, pay };
  }

  getCourierJob(state: GameState, id: string): CourierJob | undefined {
    return state.courierJobs.find((j) => j.id === id);
  }

  completeCourierJob(state: GameState, id: string, finance: FinanceSystem): boolean {
    const job = this.getCourierJob(state, id);
    if (!job) return false;
    finance.credit(state, job.pay, 'job', job.title);
    state.courierJobs = state.courierJobs.filter((j) => j.id !== id);
    state.statistics.couriersCompleted += 1;
    this.bus.emit({
      type: 'job.completed',
      jobId: job.id,
      title: job.title,
      pay: job.pay,
      quality: 1,
    });
    return true;
  }
}

function nameOf(locationId: string): string {
  return LOCATIONS.find((l) => l.id === locationId)?.name ?? locationId;
}
